import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Palette, Check, X } from "lucide-react";
import { APP_THEMES, getAppTheme, setAppTheme } from "../lib/appTheme";

// Ambient theme switcher. Recolors the whole-app living background across a set
// of readable, deep palettes (content stays on dark glass, so nothing ever
// becomes unreadable). "Time of Day" follows the shipped dawn→night sky.
// On phones the picker opens as a centered, fully-visible sheet (not a dropdown
// that fell below the fold), so every option is always reachable.
const ThemePicker = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(getAppTheme);
  const [isMobile, setIsMobile] = useState(() => {
    try { return window.matchMedia("(max-width: 767px)").matches; } catch { return false; }
  });
  const ref = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => { setAppTheme(current); }, [current]);

  useEffect(() => {
    let mql;
    try {
      mql = window.matchMedia("(max-width: 767px)");
      const onChange = (e) => setIsMobile(e.matches);
      mql.addEventListener ? mql.addEventListener("change", onChange) : mql.addListener(onChange);
      return () => { mql.removeEventListener ? mql.removeEventListener("change", onChange) : mql.removeListener(onChange); };
    } catch { return undefined; }
  }, []);

  // When the active explorer changes, load THAT explorer's saved theme.
  useEffect(() => {
    const relearn = () => setCurrent(getAppTheme());
    window.addEventListener("active-learner-changed", relearn);
    return () => window.removeEventListener("active-learner-changed", relearn);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const active = APP_THEMES.find((t) => t.id === current) || APP_THEMES[0];

  const optionList = (
    <>
      {APP_THEMES.map((t) => (
        <button
          key={t.id}
          onClick={() => { setCurrent(t.id); setOpen(false); }}
          className={`w-full flex items-center gap-3 px-3 py-3 hover:bg-white/5 transition-all ${current === t.id ? "bg-white/8" : ""}`}
          data-testid={`theme-${t.id}`}
        >
          <div className="w-8 h-8 rounded-lg border-2 flex-shrink-0" style={{ background: t.swatch, borderColor: current === t.id ? t.accent : "rgba(255,255,255,0.15)" }} />
          <div className="text-left flex-1 min-w-0">
            <p className="text-white text-sm font-fredoka font-bold">{t.label}</p>
            <p className="text-white/40 text-[11px] truncate">{t.desc}</p>
          </div>
          {current === t.id && <Check size={16} style={{ color: t.accent }} className="ml-auto flex-shrink-0" />}
        </button>
      ))}
    </>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 rounded-lg transition-all text-white/60 hover:text-white hover:bg-white/10 flex items-center gap-1"
        title="Change background theme"
        data-testid="theme-picker-btn"
      >
        <Palette size={16} />
        <span className="w-3.5 h-3.5 rounded-full border border-white/30" style={{ background: active.swatch }} />
      </button>

      {/* Desktop: anchored dropdown */}
      {open && !isMobile && (
        <div className="absolute right-0 top-full mt-2 w-60 rounded-xl border-2 border-white/15 bg-slate-900/95 shadow-2xl z-50 overflow-hidden max-h-[70vh] overflow-y-auto" data-testid="theme-picker-dropdown">
          <div className="px-3 py-2 border-b border-white/10 sticky top-0 bg-slate-900/95">
            <p className="text-white/70 text-[10px] font-bold uppercase tracking-wider">Background Theme</p>
          </div>
          {optionList}
        </div>
      )}

      {/* Mobile: fully-visible centered sheet via portal (never below the fold) */}
      {open && isMobile && createPortal(
        <div className="fixed inset-0 z-[2147483400] flex items-end sm:items-center justify-center" data-testid="theme-picker-sheet-backdrop">
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div
            ref={panelRef}
            className="relative w-full max-w-md mx-auto rounded-t-3xl sm:rounded-3xl border-2 border-white/15 bg-slate-900/98 shadow-2xl overflow-hidden max-h-[75vh] flex flex-col animate-in slide-in-from-bottom duration-200"
            data-testid="theme-picker-dropdown"
          >
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <p className="text-white/80 text-xs font-bold uppercase tracking-wider">Background Theme</p>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center" data-testid="theme-picker-close" aria-label="Close">
                <X size={16} className="text-white/70" />
              </button>
            </div>
            <div className="overflow-y-auto overscroll-contain">{optionList}</div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ThemePicker;
